import HeaderSection from '@/components/Header/HeaderSection.vue';
import tile_card from '@/components/tile_card/TIleCardView.vue';
import DetailsCard from '@/components/Details_card/DetailCardView.vue';
import OverviewSummary from '@/components/OverviewSummary/OverviewSummary.vue';
import PortfolioSuperSetChart from '@/components/PortfolioSuperSetChart/PortfolioSuperSetChart.vue';
import WelcomeBack from '@/components/WelcomeBack/WelcomeBack.vue';
import PropertyOverview from '@/components/PropertyOverview/PropertyOverview.vue';
import EmergencyContactMap from '@/components/EmergencyContactMap/EmergencyContactMap.vue';
import QuickActions from '@/components/QuickActions/QuickActions.vue';
import ServiceProvider from '@/components/ServiceProvider/ServiceProvider.vue';
import PortfolioSidebar from '@/components/PortfolioSidebar/PortfolioSidebar.vue';
import PropertySidebar from '@/components/PropertySidebar/PropertySidebar.vue';
import BolideAssistant from '@/components/BolideAssistant/BolideAssistant.vue';
import { clear_token, get_auth_session_id, get_token, get_user_profile } from '@/router';
import is_admin_user from '@/utils/auth_user';
import getTilesConfiguration from '@/data/tiles';
import { portfolio_property_name } from '@/components/Settings/property_access_utils';
import {
  ajax_json_request,
  build_table_rows,
  build_tariff_lookup,
  build_url_with_query,
  ensure_trailing_slash,
  extract_rate_records,
  extract_tariff_records,
  merge_assignment_records,
  normalize_list_rate_record,
  normalize_properties_response
} from '@/components/Settings/settings_utils';
import {
  main_dashboard_id,
  global_executive_dashboard_id,
  global_financial_dashboard_id,
  global_comparisons_dashboard_id,
  global_esg_dashboard_id,
  global_fungi_profit_share_id,
  property_specific_dashboards as imported_property_specific_dashboards,
  property_dashboards as imported_property_dashboards
} from '@/config/dashboardIds';
import ShiftSchedule from '@/components/ShiftSchedule/ShiftSchedule.vue';
import BolideLoader from '@/components/BolideLoader/BolideLoader.vue';
import SecurityDemographicsCompetition from '@/components/SecurityDemographicsCompetition/SecurityDemographicsCompetition.vue';

// Environment variables
const superset_base_domain = process.env.VUE_APP_SUPERSET_BASE_URL;
const guest_token_url = process.env.VUE_APP_GUEST_TOKEN_URL;
const superset_sdk_url = process.env.VUE_APP_SUPERSET_SDK_URL;
const properties_url = process.env.VUE_APP_PROPERTIES_URL;
const tariff_books_url = ensure_trailing_slash(process.env.VUE_APP_TARIFF_BOOKS_URL);
const tariffs_url = ensure_trailing_slash(
  process.env.VUE_APP_TARIFFS_URL || tariff_books_url.replace(/tariff-books\/?$/i, "tariffs/")
);
const property_tariff_assignments_url = ensure_trailing_slash(process.env.VUE_APP_PROPERTY_TARIFF_ASSIGNMENTS_URL);
const tariff_rates_url = ensure_trailing_slash(
  process.env.VUE_APP_RATES_URL
  || process.env.VUE_APP_TARIFF_RATES_URL?.replace(/tariff-rates\/?$/i, "rates/")
  || tariff_books_url.replace(/tariff-books\/?$/i, "rates/")
);
const active_property_storage_key = "bolide_active_property_context";

// Superset Config
const SUPERSET_CONFIG = {
  baseUrl: superset_base_domain,
  defaultDashboardIds: {
    main: main_dashboard_id
  }
};

const AUTH_CONFIG = {
  guestTokenUrl: guest_token_url
};

// Token Cache
const TOKEN_CACHE = {
  tokens: new Map(),

  get(dashboardId, authSessionId) {
    const cached = this.tokens.get(dashboardId);
    if (cached && cached.authSessionId === authSessionId && Date.now() < cached.expiresAt) return cached.token;
    if (cached) this.tokens.delete(dashboardId);
    return null;
  },

  set(dashboardId, token, authSessionId, expiresInMs = 55 * 60 * 1000) {
    this.tokens.set(dashboardId, {
      token,
      authSessionId,
      expiresAt: Date.now() + expiresInMs
    });
  },

  clear() { this.tokens.clear(); },

  cleanup() {
    const now = Date.now();
    for (const [dashboardId, data] of this.tokens.entries()) {
      if (now >= data.expiresAt || data.authSessionId !== get_auth_session_id()) this.tokens.delete(dashboardId);
    }
  }
};

function safeParseMaybeDoubleEncodedJSON(text) {
  let parsed = JSON.parse(text);
  if (typeof parsed === 'string') parsed = JSON.parse(parsed);
  return parsed;
}

export default {
  name: 'DashboardView',

  components: {
    tile_card,
    HeaderSection,
    DetailsCard,
    OverviewSummary,
    PortfolioSuperSetChart,
    WelcomeBack,
    PropertyOverview,
    EmergencyContactMap,
    QuickActions,
    ServiceProvider,
    PortfolioSidebar,
    PropertySidebar,
    BolideAssistant,
    ShiftSchedule,
    BolideLoader,
    SecurityDemographicsCompetition
  },

  data() {
    return ({
      // View State
      currentView: 'main',
      current_dashboard_type: 'global',
      active_menu_item: 'portfolio-comparisons',

      // Data
      tiles: [],
      all_tiles: [],
      property_assignment_lookup: {},
      tariff_rows_by_property_id: {},
      tariff_rows_by_property_name: {},
      breadcrumb: [],
      selected_property: null,
      selected_compare_property_ids: [],

      // UI State
      iframeLoading: false,
      iframeError: false,
      show_all: false,
      rightPanelCollapsed: false,
      isMobile: false,
      windowWidth: 0,

      // Superset State
      sdkLoaded: false,
      sdkConfigured: false,
      initializationComplete: false,
      authToken: null,
      authSessionId: null,
      isComponentActive: false,
      embeddedDashboards: new Set(),
      tokenRefreshInProgress: false,
      showAssistant: false,
      sidebar_collapsed: true,
      min_loader_time: 3500,
      loader_start_time: null,
      is_loading: true,
      is_mobile: window.innerWidth < 768,
      mobile_sidebar_open: false,
      fallbackLoadingTimerId: null,

      // Dashboard Mappings
      global_dashboards: ({
        'executive-summary': global_executive_dashboard_id,
        'financial-performance': global_financial_dashboard_id,
        'portfolio-comparisons': global_comparisons_dashboard_id,
        'esg-sustainability': global_esg_dashboard_id,
        'fungi-profit-share': global_fungi_profit_share_id
      }),

      // Property dashboard mapping - Universal dashboards (fallback)
      property_dashboards: imported_property_dashboards,

      // Property-specific dashboard mappings (now loaded from config)
      property_specific_dashboards: imported_property_specific_dashboards,
    });
  },

  computed: {
    current_user_profile() {
      return get_user_profile() || {};
    },

    assistant_scope() {
      return this.current_dashboard_type === 'property' && this.selected_property
        ? this.get_property_assistant_scope()
        : this.get_portfolio_assistant_scope();
    },

    comparison_property_options() {
      if (this.current_dashboard_type !== 'global') {
        return [];
      }

      const seen = new Set();
      return (this.tiles || [])
        .map((tile) => this.get_comparison_property_option(tile, seen))
        .filter(Boolean);
    },

    can_view_portfolio() {
      return this.can_view_all_properties || this.has_portfolio_assignment;
    },

    can_view_all_properties() {
      return is_admin_user(this.current_user_profile);
    },

    has_portfolio_assignment() {
      return this.get_assigned_property_refs().some((property_ref) => {
        return this.is_portfolio_assignment_ref(property_ref);
      });
    },

    currentCrimeStats() {
      if (!this.selected_property || !this.selected_property.crimeStats) {
        return ({});
      }
      return this.selected_property.crimeStats;
    },

    visible_tiles() {
      return this.show_all ? this.tiles : this.tiles.slice(0, 3);
    },

    showRightPanel() {
      return this.isMobile || !this.rightPanelCollapsed;
    },

    live_properties_count() {
      return (this.tiles || []).filter(tile => tile?.is_live).length;
    },

    portfolio_breadcrumb_title() {
      if (this.current_dashboard_type !== 'global') {
        return 'Portfolio Dashboard';
      }

      if (!this.can_view_all_properties && this.has_portfolio_assignment) {
        return 'Portfolio Dashboard';
      }

      return `Portfolio Dashboard (${this.live_properties_count} Live Properties)`;
    },

    current_dashboard_id() {
      if (this.current_dashboard_type === 'global') {
        return this.global_dashboards[this.active_menu_item] || global_executive_dashboard_id;
      }

      if (this.selected_property) {
        const propertyName = this.selected_property.title;
        const propertyDashboards = this.property_specific_dashboards[String(propertyName)];

        if (propertyDashboards && propertyDashboards[this.active_menu_item]) {
          return propertyDashboards[this.active_menu_item];
        }

        const propertyDashboardId = this.property_dashboards[this.active_menu_item];
        if (propertyDashboardId) {
          return propertyDashboardId;
        }

        return this.selected_property.dashboardId;
      }

      return main_dashboard_id;
    },

    is_property_view() {
      return this.current_dashboard_type === 'property' && this.selected_property;
    },

    /**
     * -----------------------------------------------
     * NEW COMPUTED: First Management Contact
     * -----------------------------------------------
     */
    management_contact() {
      const card = this.selected_property?.tileCards?.find(
        tileCard =>
          tileCard.type === "details" &&
          (
            tileCard.title?.toLowerCase().includes("management") ||
            tileCard.title?.toLowerCase().includes("on-site")
          )
      );

      if (!card) return null;

      // ✅ NEW STRUCTURE (contacts array)
      if (Array.isArray(card.contacts) && card.contacts.length) {
        const preferred =
          card.contacts.find(c =>
            (c.role || "").toLowerCase().includes("centre manager")
          ) ||
          card.contacts.find(c =>
            (c.status || "").toLowerCase() === "on-site"
          ) ||
          card.contacts[0];

        return ({
          full_name: preferred?.full_name || "N/A",
          phone: preferred?.phone || "N/A",
          email: preferred?.email || "N/A",
          role: preferred?.role || "Centre Manager"
        });
      }

      // ✅ OLD STRUCTURE fallback
      const managementDetails = card.details;
      if (!managementDetails) return null;

      return ({
        full_name: managementDetails.hours?.split(":")[0]?.trim() || "N/A",
        phone: managementDetails.phone || "N/A",
        email: managementDetails.email || "N/A",
        role: "Centre Manager"
      });
    }

  },

  watch: {
    current_dashboard_type(dashboard_type) {
      if (dashboard_type !== 'global') {
        this.selected_compare_property_ids = [];
        return;
      }

      this.prune_selected_compare_properties();
    },

    comparison_property_options: {
      deep: true,
      handler() {
        this.prune_selected_compare_properties();
      }
    },
  },

  async mounted() {
    this.isComponentActive = true;
    window.addEventListener("bolide-auth-session-changed", this.handleAuthSessionChanged);
    window.addEventListener("resize", this.handle_resize);

    this.is_loading = true;
    this.loader_start_time = Date.now();
    this.all_tiles = getTilesConfiguration().map(tile => ({
      ...tile,
      is_live: tile.id.includes("(Live)")
    }));

    await this.load_property_assignment_lookup();
    await this.load_property_tariff_summary();
    this.tiles = this.get_user_assigned_tiles(this.all_tiles);

    this.handleResize();
    window.addEventListener('resize', this.handleResize);
    this.apply_initial_assigned_property_view();

    this.$nextTick(() => {
      this.initializeDashboard();
    });

    // Fallback if superset never loads
    this.fallbackLoadingTimerId = setTimeout(() => {
      if (this.is_loading) {
        this.is_loading = false;
      }
    }, 10000);

    this.tokenCleanupInterval = setInterval(() => {
      TOKEN_CACHE.cleanup();
    }, 60000);
  },

  beforeUnmount() {
    this.isComponentActive = false;
    window.removeEventListener("bolide-auth-session-changed", this.handleAuthSessionChanged);
    window.removeEventListener("resize", this.handle_resize);

    this.cleanupDashboards();
    if (this.tokenCleanupInterval) clearInterval(this.tokenCleanupInterval);
    if (this.fallbackLoadingTimerId) clearTimeout(this.fallbackLoadingTimerId);
    window.removeEventListener('resize', this.handleResize);
  },

  methods: {
    handleAuthSessionChanged(event) {
      TOKEN_CACHE.clear();

      const nextAuthSessionId = event?.detail?.auth_session_id || get_auth_session_id();
      this.authSessionId = nextAuthSessionId;
      this.authToken = get_token();

      if (!this.authToken) {
        this.iframeLoading = false;
        this.iframeError = false;
        this.is_loading = false;
      }
    },

    getPortfolioBreadcrumb() {
      return ({
        id: 'main', title: this.portfolio_breadcrumb_title
      });
    },

    normalize_assignment_text(value) {
      return String(value || "")
        .toLowerCase()
        .replace(/\(live\)/g, "")
        .replace(/[^a-z0-9]+/g, " ")
        .trim();
    },

    is_portfolio_assignment_ref(value) {
      return this.normalize_assignment_text(value) === this.normalize_assignment_text(portfolio_property_name);
    },

    get_properties_endpoint() {
      if (!properties_url) {
        return "";
      }

      return properties_url.endsWith("/") ? properties_url : `${properties_url}/`;
    },

    extract_property_collection(response_data) {
      if (Array.isArray(response_data)) {
        return response_data;
      }

      if (!response_data || typeof response_data !== "object") {
        return [];
      }

      for (const key of ["data", "results", "properties"]) {
        const value = response_data[String(key)];

        if (Array.isArray(value)) {
          return value;
        }

        const nested_collection = this.extract_property_collection(value);
        if (nested_collection.length) {
          return nested_collection;
        }
      }

      return [];
    },

    get_selected_property_id() {
      return this.get_tile_property_id(this.selected_property);
    },

    get_property_assistant_scope() {
      return ({
        level: 'property',
        property_id: this.get_selected_property_id(),
        property_name: this.selected_property.title || this.selected_property.name || null,
        domain_hint: this.active_menu_item || null,
      });
    },

    get_portfolio_assistant_scope() {
      return ({
        level: 'portfolio',
        domain_hint: this.active_menu_item || null,
        compare_property_ids: this.selected_compare_property_ids.map(Number),
      });
    },

    get_comparison_property_option(tile, seen) {
      const id = this.get_tile_property_id(tile);
      const label = this.get_tile_label(tile);
      if (!this.is_new_comparison_property(id, label, seen)) return null;

      seen.add(id);
      return ({ id, label });
    },

    get_tile_label(tile) {
      return tile?.title || tile?.name || tile?.label || "";
    },

    is_new_comparison_property(id, label, seen) {
      if (!id) return false;
      if (!label) return false;
      return !seen.has(id);
    },

    get_tile_property_id(tile) {
      const property_name = this.get_tile_label(tile);
      if (!property_name) return null;

      return this.resolve_tile_property_id(tile, property_name);
    },

    resolve_tile_property_id(tile, property_name) {
      const assignment_id = this.get_assignment_id_by_property_name(property_name);
      if (assignment_id) return assignment_id;
      return this.get_numeric_tile_property_id(tile);
    },

    get_assignment_id_by_property_name(property_name) {
      const normalized_name = this.normalize_assignment_text(property_name);
      const entry = Object.entries(this.property_assignment_lookup || {}).find(
        ([, name]) => this.normalize_assignment_text(name) === normalized_name
      );
      return entry ? Number(entry[0]) : null;
    },

    get_numeric_tile_property_id(tile) {
      const candidates = [
        tile?.property_id,
        tile?.propertyId,
        tile?.property?.id,
        tile?.id,
      ];
      return candidates
        .map(Number)
        .find((candidate) => Number.isInteger(candidate) && candidate > 0) || null;
    },

    prune_selected_compare_properties() {
      const allowed_ids = new Set(this.comparison_property_options.map((item) => Number(item.id)));
      const pruned_ids = this.selected_compare_property_ids
        .map((property_id) => Number(property_id))
        .filter((property_id) => allowed_ids.has(property_id));

      if (pruned_ids.length !== this.selected_compare_property_ids.length) {
        this.selected_compare_property_ids = pruned_ids;
      }
    },

    async load_property_assignment_lookup() {
      const endpoint = this.get_properties_endpoint();
      const auth_token = get_token();

      if (!endpoint || !auth_token) {
        this.property_assignment_lookup = {};
        return;
      }

      try {
        const response = await fetch(endpoint, {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: `Token ${auth_token}`
          }
        });

        if (!response.ok) {
          throw new Error("Unable to load property assignment lookup.");
        }

        const response_data = await response.json().catch(() => []);
        const properties = this.extract_property_collection(response_data);

        this.property_assignment_lookup = this.get_property_assignment_lookup(properties);
      } catch {
        this.property_assignment_lookup = {};
      }
    },

    async load_property_tariff_summary() {
      if (!this.can_load_property_tariff_summary()) {
        this.reset_property_tariff_summary();
        return;
      }

      try {
        const payloads = await Promise.all(this.get_property_tariff_summary_requests());
        this.index_property_tariff_rows(this.build_property_tariff_rows(payloads));
      } catch (error) {
        console.warn("Unable to load property tariff summary.", error);
        this.reset_property_tariff_summary();
      }
    },

    can_load_property_tariff_summary() {
      return Boolean(properties_url)
        && Boolean(tariffs_url)
        && Boolean(property_tariff_assignments_url)
        && Boolean(get_token());
    },

    reset_property_tariff_summary() {
      this.tariff_rows_by_property_id = {};
      this.tariff_rows_by_property_name = {};
    },

    get_tariff_summary_request_headers() {
      return ({
        Accept: "application/json",
        Authorization: `Token ${get_token()}`
      });
    },

    get_property_tariff_summary_requests() {
      const headers = this.get_tariff_summary_request_headers();
      const requests = [
        ajax_json_request(properties_url, { method: "GET", headers }),
        ajax_json_request(tariffs_url, { method: "GET", headers }),
        ajax_json_request(property_tariff_assignments_url, { method: "GET", headers }),
        ajax_json_request(this.get_assignment_history_url(), { method: "GET", headers }).catch(() => [])
      ];

      return tariff_rates_url
        ? [...requests, ajax_json_request(tariff_rates_url, { method: "GET", headers }).catch(() => [])]
        : requests;
    },

    get_assignment_history_url() {
      return build_url_with_query(property_tariff_assignments_url, {
        active_only: "false",
        include_inactive: "true"
      });
    },

    build_property_tariff_rows(payloads = []) {
      const [
        properties_payload,
        tariffs_payload,
        assignments_payload,
        assignment_history_payload,
        tariff_rates_payload = []
      ] = payloads;
      const tariffs = extract_tariff_records(tariffs_payload);

      return build_table_rows(
        normalize_properties_response(properties_payload),
        merge_assignment_records(assignments_payload, assignment_history_payload),
        build_tariff_lookup(tariffs),
        extract_rate_records(tariff_rates_payload).map((rate) => normalize_list_rate_record(rate))
      );
    },

    index_property_tariff_rows(tariff_rows = []) {
      this.tariff_rows_by_property_id = tariff_rows.reduce((lookup, row) => {
        if (row?.property_id) {
          lookup[String(row.property_id)] = row;
        }
        return lookup;
      }, {});

      this.tariff_rows_by_property_name = tariff_rows.reduce((lookup, row) => {
        const normalized_name = this.normalize_assignment_text(row?.property_name);
        if (normalized_name) {
          lookup[String(normalized_name)] = row;
        }
        return lookup;
      }, {});
    },

    get_current_property_tariff_row() {
      return this.get_current_property_tariff_row_by_id()
        || this.get_current_property_tariff_row_by_name()
        || null;
    },

    get_current_property_id() {
      return this.selected_property?.property_id || this.selected_property?.id || "";
    },

    get_current_property_tariff_row_by_id() {
      const property_id = this.get_current_property_id();

      return property_id ? this.tariff_rows_by_property_id[String(property_id)] : null;
    },

    get_current_property_tariff_row_by_name() {
      const normalized_title = this.normalize_assignment_text(this.selected_property?.title);

      return this.tariff_rows_by_property_name[String(normalized_title)] || null;
    },

    get_tariff_row_value(field_name) {
      const row = this.get_current_property_tariff_row();
      const value = row?.has_assignment ? row?.[String(field_name)] : "";

      return value && value !== "-" ? value : "";
    },

    has_loaded_tariff_summary() {
      return Object.keys(this.tariff_rows_by_property_id).length > 0
        || Object.keys(this.tariff_rows_by_property_name).length > 0;
    },

    has_current_property_tariff_assignment() {
      const row = this.get_current_property_tariff_row();

      return Boolean(row?.has_assignment);
    },

    get_property_assignment_lookup(properties) {
      return properties.reduce((lookup, property) => {
        return this.add_property_to_assignment_lookup(lookup, property);
      }, {});
    },

    add_property_to_assignment_lookup(lookup, property) {
      const property_id = property?.id ?? property?.property_id ?? "";
      const property_name = property?.name || property?.property_name || property?.title || "";

      if (property_id !== "" && property_name) {
        lookup[String(property_id)] = property_name;
      }

      return lookup;
    },

    get_assigned_property_refs() {
      const profile = get_user_profile() || {};
      const raw_properties = profile.assigned_properties
        || profile.properties
        || profile.property_assignments
        || profile.profile?.properties
        || [];
      const raw_property_ids = profile.property_ids
        || profile.assigned_property_ids
        || profile.profile?.property_ids
        || [];
      const properties = Array.isArray(raw_properties) ? raw_properties : [];
      const property_ids = Array.isArray(raw_property_ids) ? raw_property_ids : [];
      const property_refs = [
        ...property_ids,
        ...property_ids.map((property_id) => this.property_assignment_lookup[String(property_id)]).filter(Boolean),
        ...properties.flatMap((property) => {
          if (typeof property === "number" || typeof property === "string") {
            return [property];
          }

          return [
            property?.id,
            property?.property_id,
            property?.name,
            property?.property_name,
            property?.title
          ];
        })
      ];

      return property_refs
        .filter((property_ref) => property_ref !== null && property_ref !== undefined && property_ref !== "")
        .map((property_ref) => String(property_ref));
    },

    tile_matches_assignment(tile, assigned_refs) {
      const tile_values = [
        tile.id,
        tile.title,
        tile.label,
        tile.dashboardId
      ].filter(Boolean);
      const normalized_tile_values = tile_values.map((value) => this.normalize_assignment_text(value));

      return assigned_refs.some((assigned_ref) => {
        if (this.is_portfolio_assignment_ref(assigned_ref)) {
          return false;
        }

        const normalized_ref = this.normalize_assignment_text(assigned_ref);

        return tile_values.some((value) => String(value) === String(assigned_ref))
          || normalized_tile_values.some((value) => value === normalized_ref);
      });
    },

    get_user_assigned_tiles(tiles) {
      if (this.can_view_all_properties || this.has_portfolio_assignment) {
        return tiles;
      }

      const assigned_refs = this.get_assigned_property_refs();

      if (!assigned_refs.length) {
        return [];
      }

      return tiles.filter((tile) => this.tile_matches_assignment(tile, assigned_refs));
    },

    apply_initial_assigned_property_view() {
      const saved_property_tile = this.get_saved_active_property_tile();

      if (saved_property_tile) {
        this.open_initial_property_view(saved_property_tile);
        return;
      }

      if (this.can_view_portfolio) {
        this.currentView = 'main';
        this.current_dashboard_type = 'global';
        this.active_menu_item = 'portfolio-comparisons';
        this.selected_property = null;
        this.clear_active_property_context();
        this.breadcrumb = [this.getPortfolioBreadcrumb()];
        return;
      }

      const assigned_refs = this.get_assigned_property_refs();
      const first_assigned_tile = assigned_refs.length ? this.tiles[0] : null;

      if (!first_assigned_tile) {
        this.currentView = 'no-assigned-properties';
        this.current_dashboard_type = 'property';
        this.active_menu_item = 'operations-overview';
        this.selected_property = null;
        this.clear_active_property_context();
        this.breadcrumb = [{ id: 'assigned-properties', title: 'Assigned Properties' }];
        return;
      }

      this.selected_property = first_assigned_tile;
      this.store_active_property_context(first_assigned_tile, 'operations-overview');
      this.current_dashboard_type = 'property';
      this.active_menu_item = 'operations-overview';
      this.currentView = first_assigned_tile.id;
      this.breadcrumb = [
        this.getPortfolioBreadcrumb(),
        { id: first_assigned_tile.id, title: first_assigned_tile.title }
      ];
    },

    open_initial_property_view(tile) {
      const saved_context = this.get_active_property_context();
      // 'resources' now lives on its own route, so a stale saved context
      // from before that split must not resurrect the old inline layout.
      const menu_item = (saved_context?.menuItem && saved_context.menuItem !== 'resources')
        ? saved_context.menuItem
        : 'operations-overview';

      this.selected_property = tile;
      this.current_dashboard_type = 'property';
      this.active_menu_item = menu_item;
      this.currentView = tile.id;
      this.breadcrumb = [
        this.getPortfolioBreadcrumb(),
        { id: tile.id, title: tile.title }
      ];
    },

    get_active_property_context() {
      try {
        return JSON.parse(sessionStorage.getItem(active_property_storage_key) || "null");
      } catch (error) {
        return null;
      }
    },

    get_saved_active_property_tile() {
      const saved_context = this.get_active_property_context();

      if (!saved_context) {
        return null;
      }

      return this.tiles.find((tile) => {
        return String(tile.id) === String(saved_context.id)
          || String(tile.title || "").toLowerCase() === String(saved_context.title || "").toLowerCase();
      }) || null;
    },

    shouldEmbedCurrentView(menuItem = this.active_menu_item) {
      return menuItem !== 'resources';
    },

    isConfiguredDashboardId(dashboardId) {
      const normalizedDashboardId = String(dashboardId || "").trim();
      return Boolean(normalizedDashboardId) && !normalizedDashboardId.startsWith("REPLACE_");
    },

    getCurrentDashboardContainerId() {
      return this.currentView === 'main'
        ? 'dashboard-main'
        : `dashboard-${this.currentView}`;
    },

    toggleSidebar() {
      this.sidebar_collapsed = !this.sidebar_collapsed;
    },

    handle_resize() {
      this.is_mobile = window.innerWidth < 768;
    },

    toggle_mobile_sidebar() {
      this.mobile_sidebar_open = !this.mobile_sidebar_open;
    },

    open_sidebar() {
      this.mobile_sidebar_open = true;
    },

    close_sidebar() {
      this.mobile_sidebar_open = false;
    },
    // ========== DASHBOARD INITIALIZATION ==========
    async initializeDashboard() {
      try {
        if (!this.can_view_portfolio && !this.selected_property) {
          this.is_loading = false;
          this.iframeLoading = false;
          return;
        }

        this.iframeLoading = true;
        this.iframeError = false;
        this.authToken = get_token();
        this.authSessionId = get_auth_session_id();

        if (!this.authToken) throw new Error('No authentication token found.');

        await this.initializeSupersetSDK();
        await this.configureSupersetSDK();
        if (this.shouldEmbedCurrentView()) {
          await this.embedDashboard(this.getCurrentDashboardContainerId(), this.current_dashboard_id);
        }

        // Superset is ready → hide loader
        const elapsed = Date.now() - this.loader_start_time;

        if (elapsed >= this.min_loader_time) {
          // Show loader for at least min time
          this.is_loading = false;
        } else {
          // Add delay to complete minimum duration
          const remaining = this.min_loader_time - elapsed;
          setTimeout(() => {
            this.is_loading = false;
          }, remaining);
        }

        this.initializationComplete = true;

      } catch (error) {
        console.error('Dashboard initialization failed:', error);
        this.iframeError = true;
        this.iframeLoading = false;
      }
    },

    async fetchGuestToken(dashboardId = null, { allowMainDashboardFallback = false } = {}) {
      if (!this.canUseDashboardAuth()) {
        throw this.createStaleDashboardAuthError();
      }

      const targetDashboardId = dashboardId || SUPERSET_CONFIG.defaultDashboardIds.main;
      const requestAuthToken = this.authToken;
      const requestAuthSessionId = this.authSessionId;

      if (this.tokenRefreshInProgress) {
        await new Promise(resolve => {
          const checkInterval = setInterval(() => {
            if (!this.tokenRefreshInProgress) {
              clearInterval(checkInterval);
              resolve();
            }
          }, 100);
        });

        if (!this.isCurrentDashboardAuthRequest(requestAuthToken, requestAuthSessionId)) {
          throw this.createStaleDashboardAuthError();
        }

        const cachedToken = TOKEN_CACHE.get(targetDashboardId, requestAuthSessionId);
        if (cachedToken) return ({ token: cachedToken });
      }

      this.tokenRefreshInProgress = true;

      try {
        const cachedToken = TOKEN_CACHE.get(targetDashboardId, requestAuthSessionId);
        if (cachedToken) return ({ token: cachedToken });

        const response = await fetch(AUTH_CONFIG.guestTokenUrl, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${this.authToken}`,
            'Accept': 'application/json'
          },
          body: JSON.stringify({ dashboard_id: targetDashboardId, client_app: 'newgx' })
        });

        if (!this.isCurrentDashboardAuthRequest(requestAuthToken, requestAuthSessionId)) {
          throw this.createStaleDashboardAuthError();
        }

        if (!response.ok) {
          const errorText = await response.text();
          const error = new Error(`Guest token request failed: ${response.status}, ${errorText}`);
          error.status = response.status;
          throw error;
        }

        const rawResponse = await response.text();
        const data = safeParseMaybeDoubleEncodedJSON(rawResponse);

        if (data.status === 'success' && data.data && data.data.token) {
          TOKEN_CACHE.set(targetDashboardId, data.data.token, requestAuthSessionId);
          return data.data;
        } else {
          throw new Error(data.message || 'Failed to get guest token');
        }
      } catch (error) {
        const targetDashboardId = dashboardId || SUPERSET_CONFIG.defaultDashboardIds.main;

        if (this.isAuthTokenError(error)) {
          if (!this.isCurrentDashboardAuthRequest(requestAuthToken, requestAuthSessionId)) {
            console.warn("Ignoring stale Superset guest token auth error after session changed.", error);
            throw this.createStaleDashboardAuthError();
          }

          if (!this.authToken) {
            this.handleInvalidAuthToken();
            throw new Error("Authentication expired. Please log in again.");
          }

          console.warn("Guest token request received an auth error, keeping the current session intact.", error);
          throw new Error(`Guest token request was unauthorized: ${error.message}`);
        }

        if (allowMainDashboardFallback &&
          targetDashboardId !== SUPERSET_CONFIG.defaultDashboardIds.main) {
          return this.fetchGuestToken(SUPERSET_CONFIG.defaultDashboardIds.main);
        }

        throw new Error(`Failed to fetch guest token: ${error.message}`);
      } finally {
        this.tokenRefreshInProgress = false;
      }
    },

    isAuthTokenError(error) {
      const message = String(error?.message || error || "").toLowerCase();
      return message.includes("invalid token")
        || message.includes("authentication expired")
        || message.includes("no authentication token found")
        || error?.status === 401;
    },

    createStaleDashboardAuthError() {
      const error = new Error("Ignoring stale dashboard authentication request.");
      error.staleDashboardAuth = true;
      return error;
    },

    canUseDashboardAuth() {
      return this.isComponentActive
        && this.$route?.path === "/home"
        && this.authToken
        && this.authSessionId
        && this.authToken === get_token()
        && this.authSessionId === get_auth_session_id();
    },

    isCurrentDashboardAuthRequest(authToken, authSessionId) {
      return this.isComponentActive
        && this.$route?.path === "/home"
        && authToken
        && authSessionId
        && authToken === get_token()
        && authSessionId === get_auth_session_id();
    },

    isMissingEmbeddedDashboardError(error) {
      const message = String(error?.message || error || "").toLowerCase();
      return message.includes("embeddeddashboard not found");
    },

    handleInvalidAuthToken() {
      clear_token();
      TOKEN_CACHE.clear();
      this.authToken = null;
      this.iframeLoading = false;
      this.iframeError = true;

      if (this.$route?.path !== "/login") {
        this.$router.replace("/login");
      }
    },

    async initializeSupersetSDK() {
      if (typeof window.supersetEmbeddedSdk === 'undefined') await this.loadSupersetSDK();
      this.sdkLoaded = true;
      return true;
    },

    loadSupersetSDK() {
      return new Promise((resolve, reject) => {
        if (typeof window.supersetEmbeddedSdk !== 'undefined') {
          resolve();
          return;
        }
        const script = document.createElement('script');
        script.src = superset_sdk_url;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load Superset SDK'));
        document.head.appendChild(script);
      });
    },

    async configureSupersetSDK() {
      if (!window.supersetEmbeddedSdk) throw new Error('Superset SDK not loaded');
      this.sdkConfigured = true;
    },

    // ========== DASHBOARD EMBEDDING ==========
    async embedDashboard(containerId, dashboardId, forceRefreshToken = true) {
      try {
        if (!this.canUseDashboardAuth()) {
          return;
        }

        this.iframeLoading = true;
        this.iframeError = false;

        if (!this.isConfiguredDashboardId(dashboardId)) {
          throw new Error(`Dashboard is not configured for ${this.active_menu_item}.`);
        }

        const mountPoint = document.getElementById(containerId);
        if (!mountPoint) throw new Error(`Mount point not found: ${containerId}`);

        mountPoint.innerHTML = '';

        // Clear cached token if forcing refresh
        if (forceRefreshToken) {
          TOKEN_CACHE.tokens.delete(dashboardId);
        }

        const guestTokenData = await this.fetchGuestToken(dashboardId);

        if (!window.supersetEmbeddedSdk) {
          await this.initializeSupersetSDK();
          await this.configureSupersetSDK();
        }

        await window.supersetEmbeddedSdk.embedDashboard({
          id: dashboardId,
          supersetDomain: SUPERSET_CONFIG.baseUrl,
          mountPoint: mountPoint,
          fetchGuestToken: async () => {
            try {
              const tokenData = await this.fetchGuestToken(dashboardId, {
                allowMainDashboardFallback: true
              });
              return tokenData.token || guestTokenData.token;
            } catch (error) {
              if (error?.staleDashboardAuth) {
                return guestTokenData.token || "";
              }

              if (this.isAuthTokenError(error)) {
                this.handleInvalidAuthToken();
                return guestTokenData.token || "";
              }

              throw error;
            }
          },
          dashboardUiConfig: {
            hideTitle: true,
            hideTab: true,
            filters: { visible: true, expanded: false }
          }
        }).catch((error) => {
          if (error?.staleDashboardAuth) {
            return;
          }

          if (this.isAuthTokenError(error)) {
            this.handleInvalidAuthToken();
            return;
          }

          throw error;
        });

        // Wait until Superset iframe signals it's ready
        window.addEventListener("message", event => {
          if (event.data?.type === "dashboardLoadComplete" ||
            event.data?.event === "dashboard_loaded") {

            // Hide loader if still visible
            if (this.is_loading) {
              this.is_loading = false;
            }
          }
        });

        this.iframeLoading = false;
        this.embeddedDashboards.add(containerId);
      } catch (error) {
        if (error?.staleDashboardAuth) {
          return;
        }

        if (this.isMissingEmbeddedDashboardError(error)) {
          console.warn(`Superset dashboard is not enabled for embedding: ${dashboardId}`);
        } else {
          console.error('Failed to embed dashboard:', error);
        }

        this.iframeLoading = false;
        this.iframeError = true;
      }
    },

    cleanupDashboards() {
      const containers = ['dashboard-main', 'dashboard-reports', 'dashboard-analytics'];
      containers.forEach(containerId => {
        const container = document.getElementById(containerId);
        if (container) container.innerHTML = '';
      });
      this.embeddedDashboards.clear();
    },

    // ========== NAVIGATION METHODS ==========
    handleBreadcrumbClick(breadcrumbId) {

      if (breadcrumbId === 'main') {
        if (!this.can_view_portfolio) {
          return;
        }

        // Clicked on "Portfolio Dashboard" - go back to global view
        this.goBack();
      } else {
        // Find the clicked breadcrumb item
        const clickedCrumb = this.breadcrumb.find(crumb => crumb.id === breadcrumbId);
        if (!clickedCrumb) return;

        const index = this.breadcrumb.findIndex(crumb => crumb.id === breadcrumbId);

        // If we clicked on the last item (current view), do nothing
        if (index === this.breadcrumb.length - 1) return;

        // Navigate to the clicked property
        const tile = this.findTile(breadcrumbId);
        if (tile) {
          this.selected_property = tile;
          this.store_active_property_context(tile, 'portfolio-comparisons');
          this.current_dashboard_type = 'property';

          // Default to executive summary when navigating via breadcrumb
          this.active_menu_item = 'portfolio-comparisons';
          this.currentView = breadcrumbId;

          // Trim breadcrumb to only show up to clicked item
          this.breadcrumb = this.breadcrumb.slice(0, index + 1);

          this.$nextTick(() => {
            this.embedDashboard(`dashboard-${breadcrumbId}`, this.current_dashboard_id);
          });
        }
      }
    },

    async goBack() {
      if (!this.can_view_portfolio) {
        const first_assigned_tile = this.tiles[0] || null;

        if (first_assigned_tile) {
          await this.handleTileClick(first_assigned_tile.id);
        }

        return;
      }

      // Clear token cache for property dashboards
      this.cleanupPropertyTokenCache();

      this.currentView = 'main';
      this.current_dashboard_type = 'global';
      this.active_menu_item = 'portfolio-comparisons';
      this.selected_property = null;
      this.clear_active_property_context();
      this.breadcrumb = [this.getPortfolioBreadcrumb()];
      this.cleanupDashboards();

      // Force refresh token for main dashboard
      TOKEN_CACHE.clear(); // Clear all cached tokens

      this.$nextTick(async () => {
        try {
          // Force fresh token fetch for main dashboard
          await this.embedDashboard('dashboard-main', this.current_dashboard_id);
        } catch (error) {
          console.error('Failed to load main dashboard:', error);
        }
      });
    },

    // Helper to clean property-specific tokens
    cleanupPropertyTokenCache() {
      if (this.selected_property) {
        const propertyName = this.selected_property.title;
        const propertyDashboards = this.property_specific_dashboards[String(propertyName)];

        if (propertyDashboards) {
          Object.values(propertyDashboards).forEach(dashboardId => {
            TOKEN_CACHE.tokens.delete(dashboardId);
          });
        }
      }
    },

    async handleGlobalMenuSelected(menuItem) {
      if (!this.can_view_portfolio) {
        const first_assigned_tile = this.tiles[0] || null;

        if (first_assigned_tile) {
          await this.handleTileClick(first_assigned_tile.id);
        }

        return;
      }

      this.active_menu_item = menuItem;
      this.current_dashboard_type = 'global';
      this.currentView = 'main';
      this.selected_property = null;
      this.clear_active_property_context();

      // Clear property tokens
      TOKEN_CACHE.cleanup();

      // Update breadcrumb based on menu selection
      if (menuItem === 'portfolio-comparisons') {
        this.breadcrumb = [this.getPortfolioBreadcrumb()];
      }

      this.$nextTick(async () => {
        try {
          // Force refresh token for global dashboard
          await this.embedDashboard('dashboard-main', this.current_dashboard_id, true);
        } catch (error) {
          console.error('Failed to load global dashboard:', error);
        }
      });
    },

    handlePropertyMenuSelected(data) {
      const tile = this.findTile(data.propertyId);

      if (data.menuItem === 'resources') {
        if (tile) {
          this.selected_property = tile;
          sessionStorage.setItem("tiles", JSON.stringify(this.tiles));
          sessionStorage.setItem("selected_property", JSON.stringify(tile));
        }
        this.$router.push({ name: 'resources_view' });
        return;
      }

      this.active_menu_item = data.menuItem;
      this.current_dashboard_type = 'property';

      if (!tile) return;

      this.selected_property = tile;
      this.store_active_property_context(tile, data.menuItem);
      this.currentView = data.propertyId;

      this.breadcrumb = [
        this.getPortfolioBreadcrumb(),
        { id: data.propertyId, title: `${data.propertyName}` }
      ];

      this.$nextTick(async () => {
        if (!this.shouldEmbedCurrentView(data.menuItem)) {
          this.iframeLoading = false;
          this.iframeError = false;
          return;
        }

        await this.embedDashboard(`dashboard-${data.propertyId}`, this.current_dashboard_id);

        if (data.menuItem === 'operations-overview') {
          await new Promise(resolve => setTimeout(resolve, 800));
          await this.$nextTick();

          if (this.$refs.emergencyMapRef) {
            try {
              await this.$refs.emergencyMapRef.ensureReady(8000);
            } catch (err) {
              console.warn('Emergency map init timed out:', err);
            }
          }
        }
      });
    },



    async handleTileClick(tileId) {
      const tile = this.findTile(tileId);
      if (!tile) return;

      this.selected_property = tile;
      this.current_dashboard_type = 'property';
      this.active_menu_item = 'operations-overview';
      this.store_active_property_context(tile, this.active_menu_item);

      // Set breadcrumb with proper structure
      this.breadcrumb = [
        this.getPortfolioBreadcrumb(),
        { id: tileId, title: tile.title }
      ];

      this.currentView = tileId;
      this.iframeLoading = true;
      this.iframeError = false;

      const containerId = `dashboard-${tileId}`;
      const dashboardId = this.current_dashboard_id;

      try {
        this.$nextTick(async () => {
          await this.embedDashboard(containerId, dashboardId);
        });
      } catch (error) {
        this.iframeError = true;
        this.iframeLoading = false;
      }
    },

    // ========== HELPER METHODS ==========
    findTile(tileId) {
      const search = (arr) => {
        for (let item of arr) {
          if (item.id === tileId) return item;
          if (item.children) {
            const found = search(item.children);
            if (found) return found;
          }
          if (item.tileCards && Array.isArray(item.tileCards)) {
            const foundInTileCards = search(item.tileCards);
            if (foundInTileCards) return foundInTileCards;
          }
        }
        return null;
      };
      return search(this.tiles);
    },

    store_active_property_context(tile, menuItem = this.active_menu_item) {
      if (!tile?.id) {
        return;
      }

      sessionStorage.setItem(active_property_storage_key, JSON.stringify({
        id: tile.id,
        title: tile.title || "",
        menuItem: menuItem || "operations-overview"
      }));
    },

    clear_active_property_context() {
      sessionStorage.removeItem(active_property_storage_key);
    },

    getTileCards() {
      // If we're in a property view and have selected property
      if (this.is_property_view && this.selected_property) {
        return this.selected_property.tileCards || [];
      }
      return [];
    },

    hasTileCards() {
      return this.is_property_view && this.selected_property &&
        this.selected_property.tileCards &&
        this.selected_property.tileCards.length > 0;
    },

    getCurrentTitle() {
      const tile = this.findTile(this.currentView);
      return tile ? tile.title : 'Overview';
    },

    isDetailView(tileId) {
      return tileId.includes('-') && tileId !== 'main';
    },

    // ========== UI HELPERS ==========
    handleResize() {
      this.windowWidth = window.innerWidth;
      this.isMobile = window.innerWidth < 992;
      if (this.isMobile) this.rightPanelCollapsed = false;
    },

    getMainContentClass() {
      if (this.isMobile) return 'col-12';
      return this.rightPanelCollapsed ? 'col-12' : 'col-lg-12 col-md-12 m-0 p-0';
    },

    getChildrenContentClass() {
      return ''; // no bootstrap column classes
    },

    getChildrenSidebarClass() {
      return ''; // no bootstrap column classes
    },


    toggleRightPanel() {
      if (this.isMobile) return;
      this.rightPanelCollapsed = !this.rightPanelCollapsed;
    },

    getPropertyMunicipality() {
      const tariff_municipality = this.get_tariff_row_value("municipality_name")
        || this.get_tariff_row_value("authority");

      if (tariff_municipality) {
        return tariff_municipality;
      }

      if (this.has_loaded_tariff_summary()) {
        return this.has_current_property_tariff_assignment()
          ? "No municipality assigned"
          : "No tariff assigned";
      }

      if (this.selected_property) {
        const title = this.selected_property.title || '';

        if (title.includes('Ganyesa'))
          return 'Kagisano/Molopo Local Municipality - Ganyesa';

        if (title.includes('Lesedi'))
          return 'City of Ekurhuleni (CoE) - Vosloorus';

        if (title.includes('Ekuphumleni'))
          return 'Ndlambe Local Municipality - Kenton-On-Sea';

        if (title.includes('Thembokwezi'))
          return 'City of Cape Town - Cape Town';

        // ---- placeholders (data later) ----
        if (title.includes('Erica')) return 'Municipality - TBD';
        if (title.includes('Amsterdam')) return 'Municipality - TBD';
        if (title.includes('Madadeni')) return 'Municipality - TBD';
        if (title.includes('Aliwal North')) return 'Municipality - TBD';
        if (title.includes('Rustenburg')) return 'Municipality - TBD';
        if (title.includes('Ngqamakhwe')) return 'Municipality - TBD';
        if (title.includes('Mkhondo')) return 'Municipality - TBD';
      }

      return 'Municipality';
    },

    getPropertyWaterUtility() {
      if (this.selected_property) {
        const title = this.selected_property.title || '';

        if (title.includes('Lesedi'))
          return 'Ekurhuleni Water';

        if (title.includes('Thembokwezi'))
          return 'City of Cape Town Water';

        if (title.includes('Ekuphumleni'))
          return 'Ndlambe Local Municipality Water';

        if (title.includes('Ganyesa'))
          return 'Kagisano/Molopo Local Municipality Water';

        // ---- placeholders ----
        if (title.includes('Erica')) return 'Water Utility - TBD';
        if (title.includes('Amsterdam')) return 'Water Utility - TBD';
        if (title.includes('Madadeni')) return 'Water Utility - TBD';
        if (title.includes('Aliwal North')) return 'Water Utility - TBD';
        if (title.includes('Rustenburg')) return 'Water Utility - TBD';
        if (title.includes('Ngqamakhwe')) return 'Water Utility - TBD';
        if (title.includes('Mkhondo')) return 'Water Utility - TBD';
      }

      return 'Water Utility';
    },

    getPropertyTariffRegime() {
      const tariff_regime = this.get_tariff_row_value("tariff_category")
        || this.get_tariff_row_value("tariff_name");

      if (tariff_regime) {
        return tariff_regime;
      }

      if (this.has_loaded_tariff_summary()) {
        return "No tariff assigned";
      }

      if (this.selected_property) {
        const title = this.selected_property.title || '';

        if (
          title.includes('Lesedi') ||
          title.includes('Ekuphumleni') ||
          title.includes('Thembokwezi')
        ) {
          return 'TIME OF USE';
        }

        if (title.includes('Ganyesa'))
          return 'Tariff Regime - TBD';

        // ---- placeholders ----
        if (title.includes('Erica')) return 'Tariff Regime - TBD';
        if (title.includes('Amsterdam')) return 'Tariff Regime - TBD';
        if (title.includes('Madadeni')) return 'Tariff Regime - TBD';
        if (title.includes('Aliwal North')) return 'Tariff Regime - TBD';
        if (title.includes('Rustenburg')) return 'Tariff Regime - TBD';
        if (title.includes('Ngqamakhwe')) return 'Tariff Regime - TBD';
        if (title.includes('Mkhondo')) return 'Tariff Regime - TBD';
      }

      return 'Tariff Information';
    }
  }
};
